import { useMemo } from 'react';
import { reconcile, summarize } from '../../components/billingReconciliation/reconcile.js';
import { categorizeRow } from '../../components/billingReconciliation/filters.js';
import { applyVarianceActionTotals } from '../../components/billingReconciliation/varianceActionTotals.js';
import { brandLabel, varianceDriverLabel } from '../../components/billingReconciliation/varianceSummaryTableModel.js';
import { isBillingReconciliationMockMode } from '../api/billingReconciliationApi.js';
import { useBillingReconciliationVariancePages } from './useBillingReconciliationVariancePages.js';

const EMPTY_API_ROWS = [];
const VARIANCE_LIST_LIMIT = 500;

const normalizeNote = (note) => ({
  id: note.id,
  text: note.text,
  author: note.authorName || note.author_name || 'Unknown',
  ts: note.createdAt || note.created_at,
  updatedAt: note.updatedAt || note.updated_at,
  status: note.isDraft || note.is_draft ? 'draft' : 'final',
});

const reviewFromApiRow = (row, localReview) => ({
  ...(localReview || {}),
  category: localReview?.category ?? row.reviewDriver ?? row.review_driver ?? null,
  remediation: localReview?.remediation ?? row.remediation ?? null,
  reviewed: Boolean(localReview?.reviewed ?? row.reviewed),
  notes: localReview?.notes || (Array.isArray(row.notes) ? row.notes.map(normalizeNote) : []),
  inheritedFromRound: localReview?.inheritedFromRound ?? row.inheritedFromRound ?? row.inherited_from_round ?? null,
  inheritedFromDate: localReview?.inheritedFromDate ?? row.inheritedFromDate ?? row.inherited_from_date ?? null,
  inheritedResolved: Boolean(localReview?.inheritedResolved ?? row.inheritedResolved ?? row.inherited_resolved),
  updatedAt: row.updatedAt || row.updated_at || localReview?.updatedAt || null,
});

const statusFromApiRow = (row) => {
  if (row.status) return row.status;
  const driver = row.autoDriver || row.auto_driver || '';
  if (driver === 'BAC_IN_CPQ_ONLY') return 'missing-gm';
  if (driver === 'BAC_IN_GM_ONLY') return 'missing-cpq';
  if (driver === 'NO_DATA') return 'missing-multiple';
  return 'variance';
};

const DRIVER_LABELS = {
  GM_NOT_BILLING_CPQ_BILLING: 'GM not billing, CPQ billing',
};

const varianceDriverFromApiRow = (row) => {
  if (row.varianceDriver) return row.varianceDriver;
  const driver = row.autoDriver || row.auto_driver;
  if (!driver) return null;
  return { label: DRIVER_LABELS[driver] || driver, color: 'default' };
};

const accountDisplayName = (summary = null) => {
  if (!summary) return '';
  const displayName = summary.displayName || '';
  const accounts = Array.isArray(summary.accounts) ? summary.accounts : [];
  if (!/^\d+\s+Salesforce accounts$/i.test(displayName)) return displayName;
  const names = accounts.map((account) => account.salesforceAccountName).filter(Boolean);
  if (!names.length) return displayName;
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
};

const normalizeActions = (row = {}) => (
  Array.isArray(row.varianceActions)
    ? row.varianceActions
    : Array.isArray(row.variance_actions)
      ? row.variance_actions
      : []
).map((action) => ({
  ...action,
  saveStatus: action.saveStatus || action.status,
}));

const nullableNumber = (value) => (
  value === undefined || value === null || value === '' ? null : Number(value)
);

const rowWithDisplayTotals = (row = {}) => {
  const varianceActions = normalizeActions(row);
  const fallbackDeltas = row.deltas || {
    gmVsCpq: Number(row.deltaGmCpq || row.delta_gm_cpq || 0),
    gmVsDpe: Number(row.deltaGmDpe || row.delta_gm_dpe || 0),
    cpqVsDpe: Number(row.deltaCpqDpe || row.delta_cpq_dpe || 0),
  };
  const hasTableTotals = row.gmTotal !== undefined
    || row.gm_total !== undefined
    || row.cpqTotal !== undefined
    || row.cpq_total !== undefined
    || row.dpeTotal !== undefined
    || row.dpe_total !== undefined;
  if (!hasTableTotals && !varianceActions.length) {
    return {
      ...row,
      deltas: fallbackDeltas,
      varianceActions,
      appSheetTotal: nullableNumber(row.appSheetTotal ?? row.app_sheet_total),
    };
  }
  const displayTotals = applyVarianceActionTotals({
    gmTotal: row.gmTotal ?? row.gm_total ?? 0,
    cpqTotal: row.cpqTotal ?? row.cpq_total ?? 0,
    dpeTotal: row.dpeTotal ?? row.dpe_total ?? 0,
  }, varianceActions, {
    cpqRows: Array.isArray(row.cpqLineItems) ? row.cpqLineItems : [],
  });
  return {
    ...row,
    ...displayTotals,
    varianceActions,
    appSheetTotal: nullableNumber(row.appSheetTotal ?? row.app_sheet_total),
  };
};

const accountSearchText = (summary = {}) => {
  const safeSummary = summary || {};
  return [
  safeSummary.displayName,
  ...(Array.isArray(safeSummary.accounts) ? safeSummary.accounts : [])
    .flatMap((account) => [account.salesforceAccountName, account.salesforceAccountId]),
  ].filter(Boolean).join(' ');
};

const rowSearchText = (row = {}) => [
  row.bac,
  row.dealerName,
  row.dealerDisplayName,
  row.dpeEnrollmentStatusCd,
  row.brand,
  brandLabel(row),
  row.status,
  row.autoDriver,
  row.reviewDriver,
  varianceDriverLabel(row),
  accountSearchText(row.accountMatchSummary),
].filter(Boolean).join(' ').toLowerCase();

const reconcileApiRow = (row, localReview) => {
  const displayRow = rowWithDisplayTotals(row);
  const accountSummary = row.accountMatchSummary || row.account_match_summary || null;
  return {
    ...displayRow,
    status: statusFromApiRow(row),
    accountMatchSummary: accountSummary,
    dealerDisplayName: row.dealerName
      || row.dealer_name
      || row.dpeDealerName
      || row.dpe_dealer_name
      || accountDisplayName(accountSummary)
      || '',
    gmLineItems: Array.isArray(row.gmLineItems) ? row.gmLineItems : [],
    cpqLineItems: Array.isArray(row.cpqLineItems) ? row.cpqLineItems : [],
    dpeLineItems: Array.isArray(row.dpeLineItems) ? row.dpeLineItems : [],
    trailingWindow: Array.isArray(row.trailingWindow)
      ? row.trailingWindow
      : [row.serviceMonth || row.service_month].filter(Boolean),
    deltas: displayRow.deltas,
    varianceDriver: varianceDriverFromApiRow(row),
    review: reviewFromApiRow(row, localReview),
  };
};

const summaryFromRun = (runSummary, varianceSummary) => {
  if (!runSummary) return varianceSummary;
  return {
    ...varianceSummary,
    totalBacs: Number(runSummary.totalBacs ?? varianceSummary.totalBacs),
    matching: Number(runSummary.matching ?? varianceSummary.matching),
    toReview: Number(runSummary.toReview ?? varianceSummary.toReview),
    reviewed: Number(runSummary.reviewed ?? varianceSummary.reviewed),
    remainingGmCpqAbs: Number(runSummary.remainingGmCpqAbs ?? runSummary.remainingAbs ?? varianceSummary.remainingGmCpqAbs),
    clearedGmCpqAbs: Number(runSummary.clearedGmCpqAbs ?? runSummary.clearedAbs ?? varianceSummary.clearedGmCpqAbs),
    totalGmCpqAbs: Number(runSummary.totalGmCpqAbs ?? runSummary.totalAbs ?? varianceSummary.totalGmCpqAbs),
    recurringSuggestions: runSummary.recurringSuggestions || varianceSummary.recurringSuggestions || null,
  };
};

// Derives everything the page UI cares about from raw inputs:
// - reconciled rows with reviews attached
// - top-level summary
// - filtered subset (active filter chips + search text)
// - per-category row counts for the filter chip badges
export const useReconciliationView = ({
  gmRows,
  cpqRows,
  serviceMonth,
  threshold,
  dpeDealers = [],
  reviews,
  activeFilters,
  search,
  runId,
  runSummary = null,
}) => {
  const mockMode = isBillingReconciliationMockMode();
  const explicitProcessingStatus = runSummary?.processingStatus || runSummary?.processing_status;
  const isProcessingRun = !mockMode && (
    explicitProcessingStatus
      ? explicitProcessingStatus === 'processing'
      : runSummary?.processing === true
  );
  const variancesQuery = useBillingReconciliationVariancePages(
    { runId, externalContextMode: 'table', limit: VARIANCE_LIST_LIMIT },
    { enabled: Boolean(runId) && !mockMode && !isProcessingRun },
  );

  const apiRows = variancesQuery.data || EMPTY_API_ROWS;
  const varianceListMeta = apiRows.meta || null;
  const reconciled = useMemo(
    () => {
      if (!mockMode) {
        if (isProcessingRun) return [];
        if (!variancesQuery.isSuccess) return [];
        return apiRows.map((r) => reconcileApiRow(r, reviews[r.bac]));
      }
      return reconcile({
        gmRows,
        cpqRows,
        dpeDealers,
        serviceMonth,
        threshold,
      }).map((r) => ({ ...r, review: reviews[r.bac] || null }));
    },
    [mockMode, isProcessingRun, variancesQuery.isSuccess, apiRows, gmRows, cpqRows, dpeDealers, serviceMonth, threshold, reviews],
  );

  const summary = useMemo(() => summaryFromRun(
    mockMode ? null : runSummary,
    summarize(reconciled),
  ), [mockMode, runSummary, reconciled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reconciled.filter((r) => {
      if (!activeFilters.has(categorizeRow(r))) return false;
      if (!q) return true;
      return rowSearchText(r).includes(q);
    });
  }, [reconciled, activeFilters, search]);

  const categoryCounts = useMemo(() => {
    const counts = { variance: 0, expected_cancel: 0, missing: 0, reviewed: 0, match: 0 };
    for (const r of reconciled) counts[categorizeRow(r)] += 1;
    return counts;
  }, [reconciled]);

  return {
    reconciled,
    summary,
    filtered,
    categoryCounts,
    isLoading: variancesQuery.isLoading || isProcessingRun,
    error: variancesQuery.error,
    refetch: variancesQuery.refetch,
    varianceListMeta,
  };
};
